import React, { useEffect, useRef, useState } from "react";
import Data from "../../assets/dataStorag.webp";
import DataHover from "../../assets/dataStorage2.webp";
import cloud from "../../assets/cloud_services1.webp";
import CloudHover from "../../assets/cloudservices2.webp";
import Software from "../../assets/software1.webp";
import SoftwareHover from "../../assets/software2.webp";
import Disaster from "../../assets/disaster1.webp";
import DisasterHover from "../../assets/disaster2.webp";
import building from "../../assets/building.webp";
import { motion, useAnimation } from "framer-motion";

const OurServices = () => {
  const [dataStorageHover, setDataStorageHover] = useState(false);
  const [cloudServiceHover, setCloudServiceHover] = useState(false);
  const [softwareDevHover, setSoftwareDevHover] = useState(false);
  const [disasterRecHover, setDisasterRecHover] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const controls = useAnimation();

  // Define animation variants
  const variants = {
    hidden: { y: "400%", opacity: 0, scale: 1.9 }, // Initial state (off-screen)
    visible: { y: 0, opacity: 1, scale: 1 }, // Final state (on-screen)
  };

  useEffect(() => {
    // Create an observer for the entire section to trigger animation
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Reset to hidden state first, then animate to visible
            controls.set("hidden");
            controls.start("visible");
            setIsVisible(true);
          } else {
            // When section is out of view, reset the animation state
            controls.set("hidden");
            setIsVisible(false);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when just 10% of the section is visible
        rootMargin: "0px 0px -20% 0px", // Negative bottom margin to trigger earlier
      }
    );

    if (sectionRef.current) {
      sectionObserver.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        sectionObserver.unobserve(sectionRef.current);
      }
    };
  }, [controls]);

  return (
    <div ref={sectionRef} className="relative overflow-hidden py-32">
      <div className="w-full">
        {/* Title Container */}
        <motion.div
          ref={titleRef}
          initial="hidden" // Initial animation state
          animate={controls} // Control animation with `controls`
          variants={variants} // Animation variants
          transition={{ duration: 1, ease: "easeOut" }} // Animation duration and easing
          className="title-container absolute left-0 right-0 top-[50px] pb-20"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white text-center">
            What We Offer
          </h2>
        </motion.div>

        {/* Center Image Container - Hidden on Mobile */}
        <div className="pt-32 max-w-4xxl mx-auto relative h-[700px] hidden sm:block">
          <div className="w-full h-full rounded-lg flex justify-center items-center ">
            <img
              src={building}
              alt="Digital city building"
              className="hidden sm:block sm:w-1/2 lg:w-1/2"
            />
          </div>

          {/* Data Storage */}
          <ServiceCard
            top="8%"
            left="20%"
            image={Data}
            hoverImage={DataHover}
            title="Data Storage & Hosting Solutions"
            description="Leverage our high-performance servers and cloud solutions for secure, reliable, and fast data storage. Whether it's for backup, hosting, or long-term storage, we have you covered."
            isHovered={dataStorageHover}
            onMouseEnter={() => setDataStorageHover(true)}
            onMouseLeave={() => setDataStorageHover(false)}
          />

          {/* Cloud Services */}
          <ServiceCard
            top="8%"
            right="20%"
            image={cloud}
            hoverImage={CloudHover}
            title="Cloud Services"
            description="Offering cloud hosting, private cloud, and hybrid solutions to optimize your resources, reduce costs, and scale effectively without compromising security or performance."
            isHovered={cloudServiceHover}
            onMouseEnter={() => setCloudServiceHover(true)}
            onMouseLeave={() => setCloudServiceHover(false)}
          />

          {/* Software Development */}
          <ServiceCard
            bottom="15%"
            left="10%"
            image={Software}
            hoverImage={SoftwareHover}
            title="Software Development & IT Consulting"
            description="From custom software development to enterprise-level solutions, we help businesses innovate and streamline their processes. Our IT consulting services guide your organization in adopting the right technologies for growth."
            isHovered={softwareDevHover}
            onMouseEnter={() => setSoftwareDevHover(true)}
            onMouseLeave={() => setSoftwareDevHover(false)}
          />

          {/* Disaster Recovery */}
          <ServiceCard
            bottom="15%"
            right="10%"
            image={Disaster}
            hoverImage={DisasterHover}
            title="Disaster Recovery & Backup Solutions"
            description="Protect your critical business data with our comprehensive disaster recovery and backup strategies, ensuring business continuity even in the face of unforeseen events."
            isHovered={disasterRecHover}
            onMouseEnter={() => setDisasterRecHover(true)}
            onMouseLeave={() => setDisasterRecHover(false)}
          />
        </div>

        {/* Mobile View - Stacked Service Cards */}
        <div className="sm:hidden mt-20 px-4">
          <ServiceCardMobile
            image={Data}
            hoverImage={DataHover}
            title="Data Storage & Hosting Solutions"
            description="Leverage our high-performance servers and cloud solutions for secure, reliable, and fast data storage. Whether it's for backup, hosting, or long-term storage, we have you covered."
          />
          <ServiceCardMobile
            image={cloud}
            hoverImage={CloudHover}
            title="Cloud Services"
            description="Offering cloud hosting, private cloud, and hybrid solutions to optimize your resources, reduce costs, and scale effectively without compromising security or performance."
          />
          <ServiceCardMobile
            image={Software}
            hoverImage={SoftwareHover}
            title="Software Development & IT Consulting"
            description="From custom software development to enterprise-level solutions, we help businesses innovate and streamline their processes. Our IT consulting services guide your organization in adopting the right technologies for growth."
          />
          <ServiceCardMobile
            image={Disaster}
            hoverImage={DisasterHover}
            title="Disaster Recovery & Backup Solutions"
            description="Protect your critical business data with our comprehensive disaster recovery and backup strategies, ensuring business continuity even in the face of unforeseen events."
          />
        </div>
      </div>
    </div>
  );
};

// Reusable ServiceCard Component for Desktop
const ServiceCard = ({
  top,
  bottom,
  left,
  right,
  image,
  hoverImage,
  title,
  description,
  isHovered, // Passed as prop
  onMouseEnter, // Passed as prop
  onMouseLeave, // Passed as prop
}) => {
  return (
    <div
      className="absolute group hidden sm:block"
      style={{ top, bottom, left, right }}
    >
      <div
        className="relative w-40 h-40 mx-auto"
        onMouseEnter={onMouseEnter} // Use prop directly
        onMouseLeave={onMouseLeave} // Use prop directly
      >
        <div className="absolute w-full h-full rounded-full flex items-center justify-center ">
          <img
            src={image}
            alt={title}
            className="absolute sm:w-[75%] lg:w-[85%] rounded-full object-cover cursor-pointer"
          />
          <img
            src={hoverImage}
            alt={`${title} Hover`}
            className={`absolute sm:w-[75%] lg:w-[85%] rounded-full object-cover cursor-pointer transition-all duration-400 ${
              isHovered ? "opacity-100 scale-100" : "opacity-0 scale-0"
            }`}
            style={{ transformOrigin: "center" }}
          />
        </div>
      </div>
      <h3 className="text-center text-white text-lg mt-2 max-w-[190px] mx-auto">
        {title}
      </h3>

      {/* Tooltip with Conditional Rendering */}
      {isHovered && (
        <div
          className={`w-[250%] absolute top-1/2 -translate-y-1/2 z-10 ${
            left ? "left-full" : "right-full"
          } mr-4 bg-white/10 backdrop-blur-lg rounded-lg p-4 w-64 border border-white/20 shadow-xl`}
        >
          <h4 className="text-white font-semibold mb-2 text-2xl">{title}</h4>
          <p className="text-white text-md pt-2">{description}</p>
        </div>
      )}
    </div>
  );
};

// Reusable ServiceCard Component for Mobile
const ServiceCardMobile = ({ image, hoverImage, title, description }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mb-8">
      <div
        className="flex flex-col items-center cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="relative w-32 h-32">
          <div className="absolute w-full h-full rounded-full flex items-center justify-center">
            <img
              src={image}
              alt={title}
              className="absolute w-[85%] rounded-full object-cover"
            />
            <img
              src={hoverImage}
              alt={`${title} Hover`}
              className={`absolute w-[85%] rounded-full object-cover transition-all duration-400 ${
                isExpanded ? "opacity-100 scale-100" : "opacity-0 scale-0"
              }`}
              style={{ transformOrigin: "center" }}
            />
          </div>
        </div>
        <h3 className="text-center text-white text-lg mt-2">{title}</h3>
      </div>

      {/* Expandable Description */}
      <div
        className={`bg-white/10 backdrop-blur-lg rounded-lg p-4 mt-2 border border-white/20 transition-all duration-300 overflow-hidden ${
          isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <p className="text-white text-md">{description}</p>
      </div>
    </div>
  );
};

export default OurServices;
